import { action } from "typesafe-actions";
import { ThunkAction } from "redux-thunk";
import { IStoreState } from "../../initialStoreState";
import { AnyAction } from "redux";
import {
  saveLoaderCompleted,
  saveLoaderProgress,
  showMessage,
} from "../../messages/messagesActions";
import { api } from "../../../api/api";
import { IBusinessPolicyFineArt } from "./businessPolicyFineArt.types";
import { ERROR_MESSAGES } from "../../../constants/enums";
import {
  buildBinderRequest,
  ILeadScheduleScope,
} from "../leadBinder/leadBinderEndpoints";

// Feature: lead-binder-additional-tabs
// 30s client-side bound so lead-scoped fetch surfaces the error + retry path
// deterministically (Requirements 4.5, 4.9).
const LEAD_BINDER_REQUEST_TIMEOUT_MS = 30000;

// Guard preventing a duplicate lead-scoped save from being dispatched while a save for
// the same record is already in flight.
let leadFineArtSaveInFlight = false;

export const FETCH_BUSINESS_POLICY_FINE_ART_LIST_PROGRESS =
  "FETCH_BUSINESS_POLICY_FINE_ART_LIST_PROGRESS";
export const FETCH_BUSINESS_POLICY_FINE_ART_LIST_SUCCESS =
  "FETCH_BUSINESS_POLICY_FINE_ART_LIST_SUCCESS";
export const FETCH_BUSINESS_POLICY_FINE_ART_LIST_FAILED =
  "FETCH_BUSINESS_POLICY_FINE_ART_LIST_FAILED";

export const fetchBusinessPolicyFineArtListProgress = () =>
  action(FETCH_BUSINESS_POLICY_FINE_ART_LIST_PROGRESS);
export const fetchBusinessPolicyFineArtListSuccess = (
  data: IBusinessPolicyFineArt[]
) => action(FETCH_BUSINESS_POLICY_FINE_ART_LIST_SUCCESS, { data });
export const fetchBusinessPolicyFineArtListFailed = () =>
  action(FETCH_BUSINESS_POLICY_FINE_ART_LIST_FAILED);

export const fetchBusinessPolicyFineArtListAsync =
  (
    customerPolicyId: number,
    status?: string
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(fetchBusinessPolicyFineArtListProgress());

      let finalUrl = `/policy/get-policy-business-fine-art?customer_policy_id=${customerPolicyId}`;

      if (status !== undefined && status !== "-1") {
        finalUrl += `&status=${status}`;
      }
      const res = await api.get(finalUrl);
      const data: IBusinessPolicyFineArt[] = res.data.data;
      dispatch(fetchBusinessPolicyFineArtListSuccess(data));
    } catch (err: any) {
      dispatch(fetchBusinessPolicyFineArtListFailed());
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    }
  };

export const addBusinesPolicyFineArtAsync =
  (
    data: IBusinessPolicyFineArt,
    onCallback: (isSuccess: boolean, fineArt?: IBusinessPolicyFineArt) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(saveLoaderProgress());
      //dispatch(fetchBusinessPolicyRiskProgress());
      const res = await api.post(`/policy/edit-policy-business-fine-art`, {
        ...data,
      });
      dispatch(
        showMessage({
          type: "success",
          message: "Fine art saved successfully!",
          displayAs: "snackbar",
        })
      );
      onCallback(true, res.data.data);
    } catch (err: any) {
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
      onCallback(false);
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

// Feature: lead-binder-additional-tabs
// Lead-scoped Fine Art save. Persists with `lead_id` and NO `customer_policy_id`. A
// local in-flight guard prevents a duplicate save while one is already running. On
// success it re-fetches the list by the same `lead_id` (Requirements 4.2, 4.3). On
// failure/timeout it surfaces an error; the caller retains the entered input
// (Requirement 4.6). Mirrors `addLeadBusinessRiskLocationAsync`.
export const addLeadBusinessFineArtAsync =
  (
    data: IBusinessPolicyFineArt,
    scope: ILeadScheduleScope,
    onCallback: (isSuccess: boolean, fineArt?: IBusinessPolicyFineArt) => void,
    status?: string
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    if (leadFineArtSaveInFlight) {
      return;
    }
    leadFineArtSaveInFlight = true;
    try {
      dispatch(saveLoaderProgress());
      const request = buildBinderRequest(
        "fine-art",
        { kind: "lead", leadId: scope.leadId },
        "save"
      );
      const {
        customer_id,
        customer_policy_id,
        ...rest
      } = data as IBusinessPolicyFineArt & {
        customer_id?: unknown;
        customer_policy_id?: unknown;
      };
      const res = await api.post(
        request.url,
        { ...rest, ...request.body },
        { timeout: LEAD_BINDER_REQUEST_TIMEOUT_MS }
      );
      dispatch(
        showMessage({
          type: "success",
          message: "Fine art saved successfully!",
          displayAs: "snackbar",
        })
      );
      // Re-fetch the list by the same lead_id after a successful save.
      dispatch(fetchLeadFineArtListAsync(scope, status));
      onCallback(true, res.data.data);
    } catch (err: any) {
      dispatch(
        showMessage({
          type: "error",
          message:
            err?.response?.data?.message ??
            "Fine art could not be saved. Please try again.",
          displayAs: "snackbar",
        })
      );
      onCallback(false);
    } finally {
      leadFineArtSaveInFlight = false;
      dispatch(saveLoaderCompleted());
    }
  };

// Feature: lead-binder-additional-tabs
// Lead-scoped Fine Art list fetch. Keyed by `lead_id` (never `customer_policy_id`),
// routed through the centralized endpoint map. On failure/timeout it flips the list to
// the Failed load state (retry surfaced by the sub-tab) and shows an error message
// (Requirements 4.1, 4.5, 4.9). Mirrors `fetchLeadBusinessRiskListAsync`.
export const fetchLeadFineArtListAsync =
  (
    scope: ILeadScheduleScope,
    status?: string
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(fetchBusinessPolicyFineArtListProgress());
      const request = buildBinderRequest(
        "fine-art",
        { kind: "lead", leadId: scope.leadId },
        "fetch"
      );
      let finalUrl = request.url;
      if (status !== undefined && status !== "-1") {
        finalUrl += `&status=${status}`;
      }
      const res = await api.get(finalUrl, {
        timeout: LEAD_BINDER_REQUEST_TIMEOUT_MS,
      });
      const data: IBusinessPolicyFineArt[] = res.data.data;
      dispatch(fetchBusinessPolicyFineArtListSuccess(data));
    } catch (err: any) {
      dispatch(fetchBusinessPolicyFineArtListFailed());
      dispatch(
        showMessage({
          type: "error",
          message:
            err?.response?.data?.message ??
            "Fine Art binder data could not be loaded.",
          displayAs: "snackbar",
        })
      );
    }
  };

export const FETCH_BUSINESS_POLICY_FINE_ART_PROGRESS =
  "FETCH_BUSINESS_POLICY_FINE_ART_PROGRESS";
export const FETCH_BUSINESS_POLICY_FINE_ART_SUCCESS =
  "FETCH_BUSINESS_POLICY_FINE_ART_SUCCESS";
export const FETCH_BUSINESS_POLICY_FINE_ART_FAILED =
  "FETCH_BUSINESS_POLICY_FINE_ART_FAILED";

export const fetchBusinessPolicyFineArtProgress = () =>
  action(FETCH_BUSINESS_POLICY_FINE_ART_PROGRESS);
export const fetchBusinessPolicyFineArtSuccess = (
  data: IBusinessPolicyFineArt
) => action(FETCH_BUSINESS_POLICY_FINE_ART_SUCCESS, { data });
export const fetchBusinessPolicyFineArtFailed = (errorMessage: string) =>
  action(FETCH_BUSINESS_POLICY_FINE_ART_FAILED, { errorMessage });

export const fetchBusinessPolicyFineArtAsync =
  (
    customerPolicyId: number,
    incrementNumber: number
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(fetchBusinessPolicyFineArtProgress());
      const res = await api.get(
        `/policy/get-policy-business-fine-art?customer_policy_id=${customerPolicyId}&column=incremental_number&value=${incrementNumber}`
      );
      const data: IBusinessPolicyFineArt[] = res.data.data;
      if (data.length > 0) {
        dispatch(fetchBusinessPolicyFineArtSuccess(data[0]));
      } else {
        dispatch(
          fetchBusinessPolicyFineArtFailed(
            "Oops! We couldn't find any records associated with your fine art number at the moment. Please ensure that the provided information is accurate and try again."
          )
        );
      }
    } catch (err: any) {
      dispatch(fetchBusinessPolicyFineArtFailed(ERROR_MESSAGES.SERVER_ERROR));
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    }
  };

// Feature: lead-binder-additional-tabs
// Lead-scoped single Fine Art fetch (by incremental number). Keyed by `lead_id`
// (never `customer_policy_id`) via the centralized endpoint map. On failure/timeout it
// flips the record to the Failed load state so the sub-tab can surface the error +
// retry. Mirrors `fetchLeadBusinessRiskAsync`.
export const fetchLeadFineArtAsync =
  (
    scope: ILeadScheduleScope,
    incrementNumber: number
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(fetchBusinessPolicyFineArtProgress());
      const request = buildBinderRequest(
        "fine-art",
        { kind: "lead", leadId: scope.leadId },
        "fetch"
      );
      const finalUrl = `${request.url}&column=incremental_number&value=${incrementNumber}`;
      const res = await api.get(finalUrl, {
        timeout: LEAD_BINDER_REQUEST_TIMEOUT_MS,
      });
      const data: IBusinessPolicyFineArt[] = res.data.data;
      if (data.length > 0) {
        dispatch(fetchBusinessPolicyFineArtSuccess(data[0]));
      } else {
        dispatch(
          fetchBusinessPolicyFineArtFailed(
            "Oops! We couldn't find any records associated with your fine art number at the moment. Please ensure that the provided information is accurate and try again."
          )
        );
      }
    } catch (err: any) {
      dispatch(fetchBusinessPolicyFineArtFailed(ERROR_MESSAGES.SERVER_ERROR));
      dispatch(
        showMessage({
          type: "error",
          message:
            err?.response?.data?.message ??
            "Fine Art binder data could not be loaded.",
          displayAs: "snackbar",
        })
      );
    }
  };

export const CLEAR_BUSINES_POLICY_FINE_ART_STATE =
  "CLEAR_BUSINES_POLICY_FINE_ART_STATE";
export const CLEAR_BUSINES_POLICY_FINE_ART = "CLEAR_BUSINES_POLICY_FINE_ART";

export const clearBusinessFineArtState = () =>
  action(CLEAR_BUSINES_POLICY_FINE_ART_STATE);

export const clearBusinessToolFloater = () =>
  action(CLEAR_BUSINES_POLICY_FINE_ART);
